'use client';
import { Box, List, Typography } from '@mui/material';
import { useState } from 'react';
import type { StructuredContentDocument } from '@repo/supergraph/structuredContent/types';
import { ShowMore } from '../../../ShowMore';
import { CALCULATOR_ONLY } from './constants';
import { MobileTocBar } from './MobileTocBar';
import { TableOfContentItem } from './TableOfContentItem';
import type { TableOfContentsEntry, TableOfContentsEntryType } from './types';

type TOCProps = StructuredContentDocument<{
  title: string;
  entryFilter: TableOfContentsEntryType;
  hasItemMarker: boolean;
  hasNumberPrefix: boolean;
  showMore: boolean;
  numItemsAboveFold: number;
  items: TableOfContentsEntry[];
  autoPopulateItems?: boolean;
  isCalculatorPage?: boolean;
}>;

export function TableOfContents({ props }: TOCProps) {
  const {
    title,
    hasItemMarker = false,
    hasNumberPrefix = false,
    showMore = true,
    items = [],
    numItemsAboveFold = 5,
    isCalculatorPage,
  } = props;
  const [activeItemIndex, setActiveItemIndex] = useState(-1);

  if (items.length === 0) {
    return null;
  }

  const renderToCItems = (
    <List sx={{ padding: 0 }} component={hasNumberPrefix ? 'ol' : 'ul'}>
      {items.map((tocItem, index) => {
        const itemIndex = index + 1;

        return (
          <TableOfContentItem
            key={tocItem.target}
            target={tocItem.target}
            isNumberList={hasNumberPrefix}
            isActiveItem={hasItemMarker && index === activeItemIndex}
            title={tocItem.textContent}
            itemIndex={itemIndex}
            setActiveItem={() => setActiveItemIndex(index)}
          />
        );
      })}
    </List>
  );

  return (
    <>
      <Box
        sx={{
          marginY: 3.5,
          ...(isCalculatorPage ? { display: { xs: 'none', md: 'block' } } : {}),
        }}
      >
        <Typography
          variant="smallHeadline"
          sx={{
            textTransform: 'uppercase',
          }}
        >
          {title}
        </Typography>
        <Box sx={{ marginTop: 3 }}>
          {showMore ? (
            <ShowMore
              instanceId="structured-content-toc-show-more"
              showMoreText="Show More"
              showLessText="Hide"
              maxContentHeight={numItemsAboveFold * 40}
              hideBottomGradient
            >
              {renderToCItems}
            </ShowMore>
          ) : (
            renderToCItems
          )}
        </Box>
      </Box>
      {items.length > 0 && (!CALCULATOR_ONLY || isCalculatorPage) && <MobileTocBar items={items} />}
    </>
  );
}
